import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Mail, Phone, Building, FileText, Calendar, DollarSign, Upload, Shield, ShieldCheck, StickyNote, Save, Edit2, X } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Tax certificate state
  const [showCertificateUpload, setShowCertificateUpload] = useState(false);
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificateNumber, setCertificateNumber] = useState('');
  const [certificateExpiry, setCertificateExpiry] = useState('');
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const [updatingTaxStatus, setUpdatingTaxStatus] = useState(false);

  // Notes state
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      const [customerRes, proposalsRes] = await Promise.all([
        axios.get(`/customers/${id}`),
        axios.get(`/customers/${id}/proposals`)
      ]);
      
      setCustomer(customerRes.data);
      setProposals(proposalsRes.data);
    } catch (error) {
      toast.error('Failed to load contact details');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const [updatingPaymentTerms, setUpdatingPaymentTerms] = useState(false);
  const [paymentTermsLabel, setPaymentTermsLabel] = useState('');

  const togglePaymentTerms = async () => {
    setUpdatingPaymentTerms(true);
    try {
      const approve = !customer.payment_terms_approved;
      const response = await axios.patch(`/customers/${id}/payment-terms`, {
        payment_terms_approved: approve, payment_terms_label: paymentTermsLabel || null,
      });
      setCustomer(response.data);
      toast.success(approve ? 'Customer approved for invoicing / payment terms' : 'Payment terms removed — customer must pay');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment terms');
    } finally {
      setUpdatingPaymentTerms(false);
    }
  };

  const toggleTaxExempt = async () => {
    setUpdatingTaxStatus(true);
    try {
      const newTaxExempt = !customer.tax_exempt;
      const response = await axios.patch(`/customers/${id}/tax-exempt`, {
        tax_exempt: newTaxExempt
      });
      setCustomer(response.data);
      toast.success(newTaxExempt ? 'Customer marked as tax exempt' : 'Tax exempt status removed');
      if (newTaxExempt && !response.data.tax_certificate) {
        setShowCertificateUpload(true);
      }
    } catch (error) {
      toast.error('Failed to update tax status');
    } finally {
      setUpdatingTaxStatus(false);
    }
  };

  const handleCertificateFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a PDF or image file (PNG, JPG)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setCertificateFile(file);
    }
  };

  const uploadTaxCertificate = async () => {
    if (!certificateFile) {
      toast.error('Please select a certificate file');
      return;
    }
    
    setUploadingCertificate(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        const response = await axios.post(`/customers/${id}/tax-certificate`, {
          certificate_base64: base64,
          filename: certificateFile.name,
          certificate_number: certificateNumber || null,
          expiry_date: certificateExpiry || null
        });
        setCustomer(response.data);
        toast.success('Tax certificate uploaded - customer is now tax exempt');
        setShowCertificateUpload(false);
        setCertificateFile(null);
        setCertificateNumber('');
        setCertificateExpiry('');
        setUploadingCertificate(false);
      };
      reader.readAsDataURL(certificateFile);
    } catch (error) {
      toast.error('Failed to upload certificate');
      setUploadingCertificate(false);
    }
  };

  const deleteTaxCertificate = async () => {
    if (!window.confirm('Are you sure you want to remove the tax certificate? This will also remove tax-exempt status.')) {
      return;
    }
    try {
      const response = await axios.delete(`/customers/${id}/tax-certificate`);
      setCustomer(response.data);
      toast.success('Tax certificate removed');
    } catch (error) {
      toast.error('Failed to remove certificate');
    }
  };

  // Notes functions
  const startEditingNotes = () => {
    setNotesText(customer.notes || '');
    setIsEditingNotes(true);
  };

  const cancelEditingNotes = () => {
    setNotesText('');
    setIsEditingNotes(false);
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const response = await axios.patch(`/customers/${id}/notes`, {
        notes: notesText
      });
      setCustomer(response.data);
      setIsEditingNotes(false);
      toast.success('Notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'status-draft',
      sent: 'status-sent',
      paid: 'status-paid'
    };
    return classes[status] || 'status-draft';
  };

  const getTotalSpent = () => {
    return proposals
      .filter(p => p.status === 'paid')
      .reduce((sum, p) => sum + (Number(p.total_amount) || 0), 0);
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  if (!customer) {
    return null;
  }

  return (
    <div className="dashboard" data-testid="contact-detail-page">
      <AppHeader />

      <main className="dashboard-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0 }}>
            {customer.name}
          </h1>
        </div>

        {/* Customer Info Card */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', marginBottom: '32px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '32px',
              fontWeight: '600',
              flexShrink: 0
            }}>
              {customer.name.charAt(0).toUpperCase()}
            </div>
            
            <div style={{ flex: 1 }}>
              <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>{customer.name}</h2>
              {customer.company && (
                <p style={{ fontSize: '16px', color: '#718096', marginBottom: '16px' }}>{customer.company}</p>
              )}
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Mail size={18} style={{ color: '#667eea' }} />
                  <div>
                    <p style={{ fontSize: '12px', color: '#718096' }}>Email</p>
                    <p style={{ fontSize: '14px', fontWeight: '500' }}>{customer.email}</p>
                  </div>
                </div>
                
                {customer.phone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Phone size={18} style={{ color: '#667eea' }} />
                    <div>
                      <p style={{ fontSize: '12px', color: '#718096' }}>Phone</p>
                      <p style={{ fontSize: '14px', fontWeight: '500' }}>{customer.phone}</p>
                    </div>
                  </div>
                )}
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Calendar size={18} style={{ color: '#667eea' }} />
                  <div>
                    <p style={{ fontSize: '12px', color: '#718096' }}>Customer Since</p>
                    <p style={{ fontSize: '14px', fontWeight: '500' }}>
                      {new Date(customer.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div style={{ 
                marginTop: '20px', 
                padding: '16px', 
                background: '#fffbeb', 
                borderRadius: '12px',
                border: '1px solid #fcd34d'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <StickyNote size={18} style={{ color: '#d97706' }} />
                    <span style={{ fontWeight: '600', color: '#92400e' }}>Notes</span>
                  </div>
                  {!isEditingNotes && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={startEditingNotes}
                      style={{ color: '#d97706' }}
                    >
                      <Edit2 size={14} className="mr-1" />
                      {customer.notes ? 'Edit' : 'Add Notes'}
                    </Button>
                  )}
                </div>
                
                {isEditingNotes ? (
                  <div>
                    <textarea
                      value={notesText}
                      onChange={(e) => setNotesText(e.target.value)}
                      placeholder="Add notes about this customer... (e.g., preferred contact method, special requirements, order history notes)"
                      style={{
                        width: '100%',
                        minHeight: '120px',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #d4a373',
                        fontSize: '14px',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                      data-testid="customer-notes-textarea"
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={cancelEditingNotes}
                        disabled={savingNotes}
                      >
                        <X size={14} className="mr-1" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveNotes}
                        disabled={savingNotes}
                        style={{ background: '#d97706', color: 'white' }}
                        data-testid="save-notes-btn"
                      >
                        <Save size={14} className="mr-1" />
                        {savingNotes ? 'Saving...' : 'Save Notes'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {customer.notes ? (
                      <p style={{ 
                        fontSize: '14px', 
                        color: '#78350f', 
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.6'
                      }}>
                        {customer.notes}
                      </p>
                    ) : (
                      <p style={{ fontSize: '13px', color: '#a16207', fontStyle: 'italic' }}>
                        No notes yet. Click "Add Notes" to add information about this customer.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Terms Section — admin designates customers approved for invoicing (no payment required to accept) */}
          <div style={{ marginTop: '16px', padding: '20px', background: customer.payment_terms_approved ? '#eff6ff' : '#f8fafc', borderRadius: '12px',
            border: `2px solid ${customer.payment_terms_approved ? '#93c5fd' : '#e2e8f0'}` }} data-testid="customer-payment-terms-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <DollarSign size={24} style={{ color: customer.payment_terms_approved ? '#2563eb' : '#94a3b8' }} />
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Payment Terms</h3>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }} data-testid="customer-payment-terms-status">
                    {customer.payment_terms_approved
                      ? `Approved for invoicing (${customer.payment_terms_label || 'Approved payment terms'}) — can accept proposals without paying up front`
                      : 'Must pay in full (or an admin-authorized deposit) before production'}
                  </p>
                </div>
              </div>
              {isAdmin ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {!customer.payment_terms_approved && (
                  <input value={paymentTermsLabel} onChange={(e) => setPaymentTermsLabel(e.target.value)} placeholder="e.g. Net 30"
                    data-testid="customer-payment-terms-label-input"
                    style={{ padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 8, width: 120 }} />
                )}
                <button onClick={togglePaymentTerms} disabled={updatingPaymentTerms} data-testid="customer-payment-terms-toggle"
                  style={{ padding: '8px 16px', fontSize: '14px', fontWeight: '600', borderRadius: '8px', border: 'none', cursor: 'pointer',
                    background: customer.payment_terms_approved ? '#2563eb' : '#64748b', color: 'white', opacity: updatingPaymentTerms ? 0.6 : 1 }}>
                  {updatingPaymentTerms ? 'Updating...' : (customer.payment_terms_approved ? 'Terms Approved' : 'Approve Terms')}
                </button>
              </div>
              ) : (
                <span style={{ fontSize: 12, color: '#94a3b8' }} data-testid="customer-payment-terms-admin-only">Administrators only</span>
              )}
            </div>
          </div>

          {/* Tax Status Section */}
          <div style={{ 
            marginTop: '24px', 
            padding: '20px', 
            background: customer.tax_exempt ? '#fef3c7' : '#f0fdf4',
            borderRadius: '12px',
            border: `2px solid ${customer.tax_exempt ? '#fbbf24' : '#86efac'}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {customer.tax_exempt ? (
                  <ShieldCheck size={24} style={{ color: '#f59e0b' }} />
                ) : (
                  <Shield size={24} style={{ color: '#22c55e' }} />
                )}
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Tax Status</h3>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                    {customer.tax_exempt 
                      ? 'Tax exempt - No sales tax on proposals' 
                      : '7% Florida sales tax applied to all proposals'}
                  </p>
                </div>
              </div>
              <button
                onClick={toggleTaxExempt}
                disabled={updatingTaxStatus}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  background: customer.tax_exempt ? '#f59e0b' : '#22c55e',
                  color: 'white',
                  opacity: updatingTaxStatus ? 0.6 : 1
                }}
                data-testid="customer-tax-exempt-toggle"
              >
                {updatingTaxStatus ? 'Updating...' : (customer.tax_exempt ? 'Tax Exempt' : 'Taxable')}
              </button>
            </div>

            {/* Certificate Section */}
            {customer.tax_exempt && (
              <div style={{ 
                marginTop: '16px', 
                padding: '16px', 
                background: 'white',
                borderRadius: '8px'
              }}>
                {customer.tax_certificate ? (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontWeight: '600', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileText size={18} />
                        Resale Certificate on File
                      </span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <a 
                          href={customer.tax_certificate.data} 
                          download={customer.tax_certificate.filename}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            background: '#3b82f6',
                            color: 'white',
                            borderRadius: '6px',
                            textDecoration: 'none'
                          }}
                        >
                          Download
                        </a>
                        <button
                          onClick={deleteTaxCertificate}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                          data-testid="delete-customer-certificate-btn"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', color: '#78350f' }}>
                      <p style={{ marginBottom: '4px' }}><strong>File:</strong> {customer.tax_certificate.filename}</p>
                      {customer.tax_certificate.certificate_number && (
                        <p style={{ marginBottom: '4px' }}><strong>Certificate #:</strong> {customer.tax_certificate.certificate_number}</p>
                      )}
                      {customer.tax_certificate.expiry_date && (
                        <p style={{ marginBottom: '4px' }}><strong>Expires:</strong> {new Date(customer.tax_certificate.expiry_date).toLocaleDateString()}</p>
                      )}
                      <p style={{ fontSize: '11px', color: '#a16207', marginTop: '8px' }}>
                        Uploaded by {customer.tax_certificate.uploaded_by} on {new Date(customer.tax_certificate.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div style={{ marginTop: '12px', padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', fontSize: '12px', color: '#166534' }}>
                      All new proposals for this customer will automatically be tax exempt
                    </div>
                  </div>
                ) : showCertificateUpload ? (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <span style={{ fontWeight: '600', color: '#92400e' }}>Upload Resale Certificate</span>
                      <button
                        onClick={() => setShowCertificateUpload(false)}
                        style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#92400e' }}
                      >
                        ×
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#78350f' }}>
                          Certificate File (PDF, PNG, JPG) *
                        </label>
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={handleCertificateFileChange}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '6px',
                            border: '1px solid #d4a373',
                            background: 'white',
                            fontSize: '13px'
                          }}
                          data-testid="customer-certificate-file-input"
                        />
                        {certificateFile && (
                          <p style={{ fontSize: '11px', color: '#22c55e', marginTop: '4px' }}>
                            Selected: {certificateFile.name}
                          </p>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#78350f' }}>
                            Certificate Number (Optional)
                          </label>
                          <input
                            type="text"
                            value={certificateNumber}
                            onChange={(e) => setCertificateNumber(e.target.value)}
                            placeholder="e.g., 12-345678-9"
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid #d4a373',
                              fontSize: '13px'
                            }}
                            data-testid="customer-certificate-number-input"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#78350f' }}>
                            Expiry Date (Optional)
                          </label>
                          <input
                            type="date"
                            value={certificateExpiry}
                            onChange={(e) => setCertificateExpiry(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid #d4a373',
                              fontSize: '13px'
                            }}
                            data-testid="customer-certificate-expiry-input"
                          />
                        </div>
                      </div>
                      <Button
                        onClick={uploadTaxCertificate}
                        disabled={uploadingCertificate || !certificateFile}
                        style={{ background: '#f59e0b', color: 'white', border: 'none' }}
                        data-testid="upload-customer-certificate-btn"
                      >
                        {uploadingCertificate ? 'Uploading...' : 'Upload Certificate'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#92400e' }}>No Resale Certificate</span>
                      <p style={{ fontSize: '12px', color: '#78350f', marginTop: '2px' }}>
                        Upload a certificate for compliance - all future proposals will inherit it
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowCertificateUpload(true)}
                      size="sm"
                      style={{
                        background: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      data-testid="add-customer-certificate-btn"
                    >
                      <Upload size={14} />
                      Add Certificate
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ textAlign: 'center', padding: '16px', background: '#f7fafc', borderRadius: '8px' }}>
              <p style={{ fontSize: '14px', color: '#718096', marginBottom: '4px' }}>Total Orders</p>
              <p style={{ fontSize: '32px', fontWeight: '700', color: '#667eea' }}>{proposals.length}</p>
            </div>
            
            <div style={{ textAlign: 'center', padding: '16px', background: '#f7fafc', borderRadius: '8px' }}>
              <p style={{ fontSize: '14px', color: '#718096', marginBottom: '4px' }}>Completed</p>
              <p style={{ fontSize: '32px', fontWeight: '700', color: '#22c55e' }}>
                {proposals.filter(p => p.status === 'paid').length}
              </p>
            </div>
            
            <div style={{ textAlign: 'center', padding: '16px', background: '#f7fafc', borderRadius: '8px' }}>
              <p style={{ fontSize: '14px', color: '#718096', marginBottom: '4px' }}>Total Spent</p>
              <p style={{ fontSize: '32px', fontWeight: '700', color: '#667eea' }}>
                ${getTotalSpent().toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Order History */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600' }}>Order History</h2>
            <Button
              onClick={() => navigate('/quote-builder')}
              data-testid="new-proposal-button"
            >
              New Order
            </Button>
          </div>

          {proposals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: '#718096' }}>
              <FileText size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
              <p>No orders yet for this contact</p>
            </div>
          ) : (
            <table className="proposals-table" data-testid="orders-table">
              <thead>
                <tr>
                  <th>Proposal #</th>
                  <th>Date</th>
                  <th>Quantity</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map((proposal) => (
                  <tr key={proposal.id} data-testid={`order-row-${proposal.id}`}>
                    <td data-testid={`order-number-${proposal.id}`}>{proposal.proposal_number}</td>
                    <td>{new Date(proposal.created_at).toLocaleDateString()}</td>
                    <td>{proposal.quantity ?? '—'} shirts</td>
                    <td data-testid={`order-amount-${proposal.id}`}>${Number(proposal.total_amount ?? 0).toFixed(2)}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(proposal.status)}`}>
                        {proposal.status}
                      </span>
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/proposal/${proposal.id}`)}
                        data-testid={`view-order-${proposal.id}`}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default ContactDetail;
